import { HttpClient, HttpErrorResponse, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Observable, throwError } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { ScDialogService } from '../components/sc-dialogs';
import { parseError, parseMessage } from '../helpers/error-handler-helpers';

export interface StatusMessage {
  level: number;
  code: string;
  message: string;
  data: any;
  inner: StatusMessage;
  statusMessage: Partial<StatusMessage>;
}
export interface ResponseStatus<T> {
  data: T;
  statusMessage: Partial<StatusMessage>;
  statusNumber: number;
  nextTaskDetails: T;
  error: StatusMessage;
}

export interface ResponseError {
  status: number;
  message: string;
  data: any;
}

export interface RequestOptions {
  hideSnackbar?: boolean;
  body?: any;
  headers?:
    | HttpHeaders
    | {
        [header: string]: string | string[];
      };
  observe?: 'body';
  params?:
    | HttpParams
    | {
        [param: string]: string | string[];
      };
  responseType?: 'json';
  reportProgress?: boolean;
  withCredentials?: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class RestService {
  constructor(private _httpClient: HttpClient, private _matSnackbar: MatSnackBar, private _dialogService: ScDialogService) {}

  public get<T>(url: string, options = {}): Observable<T> {
    return this._request('GET', url, options);
  }

  public put<T>(url: string, body: any | null, options: RequestOptions = {}): Observable<T> {
    return this._request('PUT', url, body, options);
  }

  public delete<T>(url: string, options = {}): Observable<T> {
    return this._request('DELETE', url, options);
  }

  public post<T>(url: string, body: any | null, options: RequestOptions = {}): Observable<T> {
    return this._request('POST', url, body, options);
  }

  private _request<T>(method: string, url: string, body: any | null, options: RequestOptions = {}): Observable<T> {
    return this._httpClient
      .request<ResponseStatus<T>>(method, url, {
        ...options,
        body
      })
      .pipe(
        tap((res: ResponseStatus<T>) => {
          if (!options.hideSnackbar) {
            this._success(url, res);
          }
        }),
        map((result: ResponseStatus<T>) => {
          if (result.data) {
            return result.data;
          }
          return result;
        }),
        catchError(err => this._error(err, body, options) as Observable<any>)
      );
  }

  private _success(url: string, res: ResponseStatus<any>) {
    const { data, statusNumber, statusMessage } = res;

    const [, endpoint] = url.split('Endpoint');
    // eslint-disable-next-line no-console
    console.info({
      action: url.includes('localhost') ? url : endpoint,
      value: data || res
    });

    if (
      !url.includes('Analytics') &&
      statusNumber === 200 &&
      (url.includes('update') || url.includes('create') || url.includes('delete'))
    ) {
      const { message } = statusMessage;
      this._matSnackbar.open(message || 'Success', 'Dismiss', {
        panelClass: 'mat-bg-success',
        duration: 2000,
        horizontalPosition: 'right'
      });
    }
  }

  private _error(error: HttpErrorResponse, data: any, options): Observable<any> {
    const errorMsg = parseError(error, data);

    if (errorMsg.statusNumber === 400 || errorMsg.statusNumber === 500) {
      const msg = parseMessage(errorMsg.error.statusMessage.message, errorMsg.error.statusMessage.code);
      this._dialogService.error(msg, 'warning');
    } else {
      if (!options.hideSnackbar) {
        this._matSnackbar.open(errorMsg.statusMessage.message, 'Dismiss', {
          panelClass: 'mat-bg-warn',
          duration: 2000
        });
      }
    }

    return throwError(errorMsg && errorMsg.error ? errorMsg.error : errorMsg);
  }
}
